// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
// Components
import Component from 'src/helpers/Component/Component';
import { ImageCarouselTheme } from './ImageCarousel.theme';
import { ComponentProps } from 'lib/types/component-props';
import { getEnum } from 'lib/utils/get-enum';
import classNames from 'classnames';
import Headline from 'src/helpers/Headline/Headline';
import { Subheadline } from 'src/helpers/Subheadline';
import { BackgroundColor, paginationStyle } from 'src/helpers/Carousel/Carousels.helper';
import SingleButton from 'src/helpers/SingleButton/SingleButton';
import { SliderWrapper } from 'src/helpers/SliderWrapper';
import ImageWrapper from 'src/helpers/Media/ImageWrapper';
import { RichTextWrapper } from 'src/helpers/RichTextWrapper';

export type ImageCarouselProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Components.General.ImageCarousel.ImageCarousel & {
    fields: {
      children: Feature.EnterpriseWeb.RenewalByAndersen.Components.General.ImageCarousel.CarouselSlide[];
    };
  } & ComponentProps;

const ImageCarousel = (props: ImageCarouselProps) => {
  const { themeData } = useTheme(ImageCarouselTheme);
  const pageinationStyle = getEnum<paginationStyle>(props.fields?.carouselStyle) || 'dots';
  const loop = props.fields?.loop?.value ?? true;
  const autoplay = props.fields?.autoplay?.value || false;
  const autoPlayInterval = props.fields?.interval?.value || 2000;

  const sliderSettings = {
    dots: true,
    enableNumberedPagination: pageinationStyle == 'numbers',
    slidesToShow: 1,
    infinite: loop,
    autoplay: autoplay,
    autoplaySpeed: autoPlayInterval,
    pauseOnFocus: true,
    pauseOnHover: true,
    dotsClass: 'slick-dots static',
    adaptiveHeight: true,
  };

  const containerWidth =
    getEnum<'fullBleed' | 'fullWidth'>(props.fields?.containerWidth) ?? 'fullWidth';
  const backgroundVariant =
    containerWidth === 'fullBleed' ? getEnum<BackgroundColor>(props.fields?.backgroundColor) : '';
  const captionClasses =
    backgroundVariant === 'black'
      ? classNames('text-white', themeData.classes.captionContainer)
      : themeData.classes.captionContainer;

  return (
    <Component
      variant={containerWidth === 'fullBleed' ? 'full' : 'lg'}
      backgroundVariant={backgroundVariant}
      dataComponent="general/imagecarousel"
      {...props}
    >
      <div className={classNames(themeData.classes.carouselWrapper)}>
        <Headline classes={themeData.classes.headlineClass} {...props} />
        <Subheadline classes={themeData.classes.Subheadline} {...props} />
        <div className="carousel-slider">
          <SliderWrapper sliderSettings={sliderSettings}>
            {props.fields?.children?.map(
              (
                childItem: Feature.EnterpriseWeb.RenewalByAndersen.Components.General.ImageCarousel.CarouselSlide,
                index: number
              ) => {
                return (
                  <div key={index}>
                    <div className={themeData.classes.sliderContainer}>
                      <ImageWrapper
                        image={childItem?.fields?.image}
                        imageLayout="responsive"
                        ratio="auto"
                      />
                      <RichTextWrapper
                        field={childItem?.fields?.caption}
                        refer="caption"
                        className={captionClasses}
                      />
                    </div>
                  </div>
                );
              }
            )}
          </SliderWrapper>
        </div>
        <SingleButton classes={themeData.classes.buttonClass} {...props} />
      </div>
    </Component>
  );
};

export default withDatasourceCheck()<ImageCarouselProps>(ImageCarousel);
